'use client';

import { changeDateRangeSelection } from '@/store/brokerMetricsHandlers';
import { useAppDispatch, useAppSelector } from '@/store/store';
import {
    getCalendarDays,
    getLatestTradingDay,
    MINIMUM_DATE,
    MONTH_NAMES,
    parseDateKey,
    thisWeekDateRange,
    WEEK_DAYS,
} from '@/utils/dateRangePicker';
import { addMonths, format, isSameDay, isSameMonth, isWeekend, isWithinInterval, startOfMonth, subMonths } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import CalendarIcon from './CalendarIcon';

type PickerMode = 'single' | 'range';

type DayState = {
    isSelected: boolean;
    isInSelectedRange: boolean;
    isCurrentMonth: boolean;
    isDisabled: boolean;
};

const getDayStateClasses = ({ isSelected, isInSelectedRange, isCurrentMonth, isDisabled }: DayState) => {
    if (isDisabled) {
        return 'cursor-not-allowed text-slate-300 hover:bg-transparent';
    }
    if (isSelected) {
        return 'bg-[#0782DA] font-semibold text-white';
    }
    if (isInSelectedRange) {
        return 'bg-[#0782DA]/15 text-slate-700';
    }
    return isCurrentMonth ? 'text-slate-800 hover:bg-slate-100' : 'text-slate-300';
};

const tabClassName = (selected: boolean) =>
    `inline-flex h-7 min-w-0 flex-1 items-center justify-center rounded-md px-2 py-0.5 text-[10px] font-semibold leading-4 text-[#555] transition-colors md:flex-none ${
        selected ? 'border-[0.5px] border-[#E3E3E3] bg-white' : 'border-[0.5px] border-transparent'
    }`;

function DateRangePicker() {
    const dispatch = useAppDispatch();
    const dateRange = useAppSelector((state) => state.brokerMetrics.dateRange);

    const [open, setOpen] = useState(false);
    const [pickerMode, setPickerMode] = useState<PickerMode>('range');
    const [draftStart, setDraftStart] = useState(dateRange.start);
    const [draftEnd, setDraftEnd] = useState(dateRange.end);
    const [rangeSelectionAnchor, setRangeSelectionAnchor] = useState<'start' | 'end'>('start');
    const [viewMonth, setViewMonth] = useState(startOfMonth(new Date()));

    const todayDate = new Date();
    const latestTradingDay = getLatestTradingDay(todayDate, MINIMUM_DATE);
    const todayKey = format(latestTradingDay, 'yyyy-MM-dd');
    const currentWeekRange = thisWeekDateRange(todayDate);

    const startDate = parseDateKey(dateRange.start);
    const endDate = parseDateKey(dateRange.end);
    const isThisWeekSelected = dateRange.start === currentWeekRange.start && dateRange.end === currentWeekRange.end;

    const years = Array.from({ length: todayDate.getFullYear() - MINIMUM_DATE.getFullYear() + 1 }, (_, index) => MINIMUM_DATE.getFullYear() + index);
    const calendarDays = getCalendarDays(viewMonth);

    const draftStartDate = parseDateKey(draftStart);
    const draftEndDate = parseDateKey(draftEnd);
    const hasInvalidOrder = Boolean(draftStartDate && draftEndDate && draftStartDate > draftEndDate);
    const canApply = pickerMode === 'single' ? Boolean(draftStartDate) : Boolean(draftStartDate && draftEndDate && !hasInvalidOrder);

    const handleToggleCustomPicker = () => {
        setDraftStart(dateRange.start);
        setDraftEnd(dateRange.end);
        setPickerMode(dateRange.start === dateRange.end ? 'single' : 'range');
        setRangeSelectionAnchor(dateRange.start === dateRange.end ? 'start' : 'end');
        setViewMonth(startOfMonth(startDate ?? todayDate));
        setOpen((prev) => !prev);
    };

    const handleSelectThisWeek = () => {
        dispatch(changeDateRangeSelection(currentWeekRange));
        setOpen(false);
    };

    const handleApply = () => {
        if (!canApply) {
            return;
        }
        const start = draftStart;
        const end = pickerMode === 'single' ? draftStart : draftEnd;
        if (!start || !end) {
            return;
        }
        dispatch(changeDateRangeSelection({ start, end }));
        setOpen(false);
    };

    const handleDayClick = (day: Date) => {
        if (day < MINIMUM_DATE || day > latestTradingDay || isWeekend(day)) {
            return;
        }
        const selectedKey = format(day, 'yyyy-MM-dd');

        if (pickerMode === 'single') {
            setDraftStart(selectedKey);
            setDraftEnd(selectedKey);
            return;
        }

        if (rangeSelectionAnchor === 'start') {
            setDraftStart(selectedKey);
            if (draftEndDate && day > draftEndDate) {
                setDraftEnd('');
                setRangeSelectionAnchor('end');
            }
            return;
        }

        if (!draftStartDate) {
            setDraftStart(selectedKey);
            setDraftEnd('');
            setRangeSelectionAnchor('end');
            return;
        }

        if (day < draftStartDate) {
            setDraftStart(selectedKey);
            setDraftEnd('');
            return;
        }

        setDraftEnd(selectedKey);
    };

    const handleMonthChange = (monthIndex: number) => {
        const next = new Date(viewMonth);
        next.setMonth(monthIndex);
        setViewMonth(startOfMonth(next));
    };

    const handleYearChange = (year: number) => {
        const next = new Date(viewMonth);
        next.setFullYear(year);
        setViewMonth(startOfMonth(next));
    };

    const handleSwitchMode = (mode: PickerMode) => {
        setPickerMode(mode);
        if (mode === 'single') {
            const fallback = draftStart || dateRange.start || todayKey;
            setDraftStart(fallback);
            setDraftEnd(fallback);
            setRangeSelectionAnchor('start');
            return;
        }
        if (draftStart && draftEnd && draftStart !== draftEnd) {
            return;
        }
        setDraftEnd('');
        setRangeSelectionAnchor('end');
    };

    let dateTriggerLabel = 'Select date';
    if (startDate && endDate?.getTime() === startDate.getTime()) {
        dateTriggerLabel = format(startDate, 'd MMM, yyyy');
    } else if (startDate && endDate) {
        dateTriggerLabel = `${format(startDate, 'd MMM')} – ${format(endDate, 'd MMM')}`;
    } else if (startDate) {
        dateTriggerLabel = format(startDate, 'd MMM, yyyy');
    }

    const isThisWeekTabSelected = isThisWeekSelected && !open;

    return (
        <div className='relative w-full md:w-auto'>
            <div
                className='flex w-full items-center gap-1 whitespace-nowrap rounded-lg bg-[#F8F8F8] p-0.5 md:inline-flex md:w-auto md:shrink-0'
                role='tablist'
                aria-label='Date range'>
                <button
                    type='button'
                    role='tab'
                    onClick={handleSelectThisWeek}
                    className={tabClassName(isThisWeekTabSelected)}
                    aria-label='This week'
                    aria-selected={isThisWeekTabSelected}>
                    This Week
                </button>
                <button
                    type='button'
                    role='tab'
                    onClick={handleToggleCustomPicker}
                    className={`${tabClassName(!isThisWeekTabSelected)} gap-1`}
                    aria-label='Open calendar'
                    aria-selected={!isThisWeekTabSelected}>
                    <CalendarIcon />
                    {dateTriggerLabel}
                </button>
            </div>

            {open && (
                <div className='absolute left-0 right-0 top-[calc(100%+8px)] z-40 w-full rounded-xl border border-slate-200 bg-white p-4 shadow-xl md:left-auto md:right-0 md:w-[24rem]'>
                    <div className='mb-3 rounded-lg border border-slate-200 bg-slate-50 p-1'>
                        <div className='grid grid-cols-2 gap-1'>
                            <button
                                type='button'
                                onClick={() => handleSwitchMode('single')}
                                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                                    pickerMode === 'single' ? 'bg-white text-[#0782DA] shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                }`}>
                                Single date
                            </button>
                            <button
                                type='button'
                                onClick={() => handleSwitchMode('range')}
                                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                                    pickerMode === 'range' ? 'bg-white text-[#0782DA] shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                }`}>
                                Date range
                            </button>
                        </div>
                    </div>

                    {pickerMode === 'range' && (
                        <div className='mb-3 grid grid-cols-2 gap-2'>
                            <button
                                type='button'
                                onClick={() => setRangeSelectionAnchor('start')}
                                className={`flex items-center justify-between rounded-lg border px-2.5 py-2 text-left ${
                                    rangeSelectionAnchor === 'start' ? 'border-[#0782DA]/50 bg-[#0782DA]/5' : 'border-slate-200'
                                }`}>
                                <span className='text-[11px] text-slate-500'>Start date</span>
                                <span className='text-sm font-medium text-slate-700'>
                                    {draftStartDate ? format(draftStartDate, 'MMM d, yyyy') : 'Select'}
                                </span>
                            </button>
                            <button
                                type='button'
                                onClick={() => setRangeSelectionAnchor('end')}
                                className={`flex items-center justify-between rounded-lg border px-2.5 py-2 text-left ${
                                    rangeSelectionAnchor === 'end' ? 'border-[#0782DA]/50 bg-[#0782DA]/5' : 'border-slate-200'
                                }`}>
                                <span className='text-[11px] text-slate-500'>End date</span>
                                <span className='text-sm font-medium text-slate-700'>
                                    {draftEndDate ? format(draftEndDate, 'MMM d, yyyy') : 'Select'}
                                </span>
                            </button>
                        </div>
                    )}

                    <div className='mb-2 flex items-center justify-between gap-2'>
                        <button
                            type='button'
                            onClick={() => setViewMonth((prev) => startOfMonth(subMonths(prev, 1)))}
                            className='rounded-md p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30'
                            disabled={startOfMonth(viewMonth) <= startOfMonth(MINIMUM_DATE)}
                            aria-label='Previous month'>
                            <ChevronLeft size={16} />
                        </button>

                        <div className='flex items-center gap-2'>
                            <select
                                value={viewMonth.getMonth()}
                                onChange={(event) => handleMonthChange(Number(event.target.value))}
                                className='h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700'
                                aria-label='Select month'>
                                {MONTH_NAMES.map((month, index) => (
                                    <option key={month} value={index}>
                                        {month}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={viewMonth.getFullYear()}
                                onChange={(event) => handleYearChange(Number(event.target.value))}
                                className='h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700'
                                aria-label='Select year'>
                                {years.map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <button
                            type='button'
                            onClick={() => setViewMonth((prev) => startOfMonth(addMonths(prev, 1)))}
                            className='rounded-md p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30'
                            disabled={startOfMonth(addMonths(viewMonth, 1)) > startOfMonth(latestTradingDay)}
                            aria-label='Next month'>
                            <ChevronRight size={16} />
                        </button>
                    </div>

                    <div className='grid grid-cols-7 gap-y-1 pb-1'>
                        {WEEK_DAYS.map((weekday) => (
                            <div key={weekday} className='text-center text-xs font-medium text-slate-500'>
                                {weekday}
                            </div>
                        ))}
                        {calendarDays.map((day) => {
                            const isDisabled = day < MINIMUM_DATE || day > latestTradingDay || isWeekend(day);
                            const isCurrentMonth = isSameMonth(day, viewMonth);
                            const isSelectedStart = draftStartDate ? isSameDay(day, draftStartDate) : false;
                            const isSelectedEnd = draftEndDate ? isSameDay(day, draftEndDate) : false;
                            const isInSelectedRange =
                                pickerMode === 'range' && draftStartDate && draftEndDate
                                    ? isWithinInterval(day, { start: draftStartDate, end: draftEndDate })
                                    : false;
                            const isSelected = pickerMode === 'single' ? isSelectedStart : isSelectedStart || isSelectedEnd;

                            return (
                                <button
                                    key={day.toISOString()}
                                    type='button'
                                    onClick={() => handleDayClick(day)}
                                    disabled={isDisabled}
                                    className={`mx-auto flex h-9 w-full max-w-[3rem] items-center justify-center rounded-md text-sm transition-colors ${getDayStateClasses(
                                        {
                                            isSelected,
                                            isInSelectedRange,
                                            isCurrentMonth,
                                            isDisabled,
                                        }
                                    )}`}>
                                    {format(day, 'd')}
                                </button>
                            );
                        })}
                    </div>

                    {hasInvalidOrder && <p className='mt-2 text-[11px] text-red-500'>End date cannot be before start date.</p>}

                    <div className='mt-3 flex items-end justify-between gap-2'>
                        <div className='text-xs text-slate-500'>
                            <p>Weekends are non-trading days</p>
                        </div>
                        <button
                            type='button'
                            onClick={handleApply}
                            disabled={!canApply}
                            className='rounded-lg bg-[#0782DA] px-5 py-2 text-base font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-100'
                            aria-label='Apply selected dates'>
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DateRangePicker;
